import React, { useState } from 'react';
import { 
  Search, 
  Package, 
  User, 
  Calendar, 
  MapPin,
  FileText,
  Download,
  Eye,
  Barcode
} from 'lucide-react';

const BatchTraceability = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);

  const completedBatches = [
    {
      batchNumber: 'NAL-2024-001',
      kitType: 'Take-Home Naloxone Kit',
      kitSku: 'NAL-HOME-01',
      quantity: 500,
      completedDate: '2024-01-17 15:30',
      assembledBy: 'Maria Rodriguez (W001)',
      completedBy: 'Sarah Johnson (W003)',
      qualityCheckedBy: 'Dr. Michael Chen (QC001)',
      baseKitInfo: {
        lotNumber: 'NAL-BASE-240115-A',
        expiry: '2026-01-15',
        supplier: 'Unique Medical Supply Ltd - Manufacturing Division'
      },
      components: [
        { sku: 'NAL-001', name: 'Naloxone Nasal Spray 4mg', lot: 'LOT-2024-001', expiry: '2025-12-31', supplier: 'Emergent BioSolutions', quantity: 1000 },
        { sku: 'INST-002', name: 'Instruction Cards (EN/FR)', lot: 'LOT-2024-002', expiry: '2025-11-30', supplier: 'Canadian Health Print', quantity: 500 },
        { sku: 'FACE-003', name: 'Face Shield/Barrier', lot: 'LOT-2024-003', expiry: '2027-10-15', supplier: 'Safety First Medical', quantity: 500 },
        { sku: 'GLOV-004', name: 'Nitrile Gloves (Pairs)', lot: 'LOT-2024-004', expiry: '2025-09-30', supplier: 'ProGlove Canada', quantity: 1000 }
      ],
      shippingInfo: {
        shippedDate: '2024-01-18 09:00',
        deliveryAddress: 'Provincial Health Authority\n789 Healthcare Drive\nToronto, ON M5V 3A8',
        trackingNumber: 'NAL-TRK-2024-001',
        carrier: 'Canada Health Logistics'
      },
      certifications: ['Health Canada Approved', 'GMP Certified', 'ISO 13485'],
      documents: ['Certificate of Analysis', 'Batch Manufacturing Record', 'Shipping Manifest', 'Quality Assurance Report']
    },
    {
      batchNumber: 'NAL-2024-002',
      kitType: 'Training Naloxone Kit',
      kitSku: 'NAL-TRAIN-01',
      quantity: 200,
      completedDate: '2024-01-16 14:15',
      assembledBy: 'James Chen (W002)',
      completedBy: 'Maria Rodriguez (W001)',
      qualityCheckedBy: 'Dr. Lisa Wang (QC002)',
      baseKitInfo: {
        lotNumber: 'NAL-BASE-240116-B',
        expiry: '2026-01-16',
        supplier: 'Unique Medical Supply Ltd - Manufacturing Division'
      },
      components: [
        { sku: 'NAL-002', name: 'Training Naloxone (Inert)', lot: 'LOT-2024-005', expiry: '2026-12-31', supplier: 'Medical Training Corp', quantity: 400 },
        { sku: 'INST-002', name: 'Instruction Cards (EN/FR)', lot: 'LOT-2024-002', expiry: '2025-11-30', supplier: 'Canadian Health Print', quantity: 200 },
        { sku: 'TRAIN-006', name: 'Training Manual', lot: 'LOT-2024-006', expiry: '2026-09-30', supplier: 'Educational Resources Ltd', quantity: 200 }
      ],
      shippingInfo: {
        shippedDate: '2024-01-17 10:30',
        deliveryAddress: 'Provincial Health Authority\n789 Healthcare Drive\nToronto, ON M5V 3A8',
        trackingNumber: 'NAL-TRK-2024-002',
        carrier: 'Canada Health Logistics'
      },
      certifications: ['Health Canada Approved', 'Training Material Certified'],
      documents: ['Certificate of Analysis', 'Batch Manufacturing Record', 'Training Certification']
    }
  ];

  const filteredBatches = completedBatches.filter(batch => 
    batch.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    batch.kitType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Batch Traceability & Records</h1>
        <p className="text-gray-600">Complete traceability for Health Canada compliance and quality assurance</p>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search batch number or kit type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Batch List */}
      <div className="space-y-6">
        {filteredBatches.map((batch) => (
          <div key={batch.batchNumber} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{batch.batchNumber}</h3>
                  <p className="text-gray-600">{batch.kitType} • {batch.quantity.toLocaleString()} kits</p>
                  <p className="text-sm text-gray-500">SKU: {batch.kitSku}</p>
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => setSelectedBatch(selectedBatch === batch.batchNumber ? null : batch.batchNumber)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    <span>{selectedBatch === batch.batchNumber ? 'Hide' : 'View'} Details</span>
                  </button>
                  <button 
                    onClick={() => alert(`Export Batch Report: ${batch.batchNumber}\n\nThis feature will generate:\n• PDF traceability report\n• Component lot tracking\n• Worker records\n• Timestamps\n• Health Canada compliance data\n\nAvailable after backend implementation.`)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    <span>Export</span>
                  </button>
                </div>
              </div>

              {/* Quick Info */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="text-sm text-gray-600">Completed</p>
                    <p className="font-medium text-gray-900">{batch.completedDate}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Assembled By</p>
                    <p className="font-medium text-gray-900">{batch.assembledBy}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Completed By</p>
                    <p className="font-medium text-gray-900">{batch.completedBy}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="text-sm text-gray-600">QC Approved By</p>
                    <p className="font-medium text-gray-900">{batch.qualityCheckedBy}</p>
                  </div>
                </div>
              </div>

              {/* Detailed View */}
              {selectedBatch === batch.batchNumber && (
                <div className="border-t border-gray-200 pt-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Base Kit Info */}
                    <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <Package className="h-5 w-5 mr-2 text-blue-600" />
                        Base Naloxone Kit Information
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <span className="text-gray-600">Lot Number:</span>
                          <p className="font-medium text-gray-900">{batch.baseKitInfo.lotNumber}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Expiry Date:</span>
                          <p className="font-medium text-gray-900">{batch.baseKitInfo.expiry}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Supplier:</span>
                          <p className="font-medium text-gray-900">{batch.baseKitInfo.supplier}</p>
                        </div>
                      </div>
                    </div>

                    {/* Shipping Info */}
                    <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <MapPin className="h-5 w-5 mr-2 text-green-600" />
                        Shipping Information
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <span className="text-gray-600">Shipped:</span>
                          <p className="font-medium text-gray-900">{batch.shippingInfo.shippedDate}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Tracking:</span>
                          <p className="font-medium text-gray-900">{batch.shippingInfo.trackingNumber}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Carrier:</span>
                          <p className="font-medium text-gray-900">{batch.shippingInfo.carrier}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Delivery Address:</span>
                          <p className="font-medium text-gray-900 whitespace-pre-line">{batch.shippingInfo.deliveryAddress}</p>
                        </div>
                      </div>
                    </div>

                    {/* Components Traceability */}
                    <div className="lg:col-span-2">
                      <h4 className="font-semibold text-gray-900 mb-4">Component Traceability</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left py-3 px-4 font-medium text-gray-900">Component</th>
                              <th className="text-left py-3 px-4 font-medium text-gray-900">Lot Number</th>
                              <th className="text-left py-3 px-4 font-medium text-gray-900">Expiry</th>
                              <th className="text-left py-3 px-4 font-medium text-gray-900">Supplier</th>
                              <th className="text-left py-3 px-4 font-medium text-gray-900">Qty Used</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {batch.components.map((component, index) => (
                              <tr key={index}>
                                <td className="py-3 px-4">
                                  <div>
                                    <p className="font-medium text-gray-900">{component.name}</p>
                                    <p className="text-sm text-gray-600">{component.sku}</p>
                                  </div>
                                </td>
                                <td className="py-3 px-4 font-medium text-gray-900">{component.lot}</td>
                                <td className="py-3 px-4 text-gray-900">{component.expiry}</td>
                                <td className="py-3 px-4 text-gray-900">{component.supplier}</td>
                                <td className="py-3 px-4 font-medium text-gray-900">{component.quantity.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Certifications & Documents */}
                    <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <FileText className="h-5 w-5 mr-2 text-red-600" />
                        Certifications
                      </h4>
                      <div className="space-y-2">
                        {batch.certifications.map((cert, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <span className="text-gray-900">{cert}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <FileText className="h-5 w-5 mr-2 text-purple-600" />
                        Available Documents
                      </h4>
                      <div className="space-y-2">
                        {batch.documents.map((doc, index) => (
                          <button key={index} className="w-full text-left p-2 rounded hover:bg-purple-100 transition-colors flex items-center justify-between">
                            <span className="text-gray-900">{doc}</span>
                            <Download className="h-4 w-4 text-purple-600" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BatchTraceability;